import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, RefreshCw, Save, Download, PlugZap, ShieldCheck, AlertTriangle, Trash2, Activity, Phone, Send, BarChart3, Link2, Chrome, Check, X } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/common";

export default function WebsiteConnEditor({ website, clientId, onChange, readOnly = false }) {
  const INIT = {
    public_url: "", wp_version: "", wp_admin_url: "", wp_manual: false, theme: "", plugins: "", plugins_visible: false,
    form_source: "", form_webhook_active: false, analytics_status: "not_connected",
    backup_status: "", backup_mode: "not_connected", last_backup: "", last_content_update: "", last_plugin_check: "", open_tech_points: "",
    ga4_property_id: "",
  };
  const norm = (w) => ({ ...INIT, ...(w || {}), uptime_pct: w?.uptime_pct ?? "",
    last_backup: (w?.last_backup || "").slice(0, 10), last_content_update: (w?.last_content_update || "").slice(0, 10),
    last_plugin_check: (w?.last_plugin_check || "").slice(0, 10) });
  const [f, setF] = useState(norm(website));
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(false);
  useEffect(() => { setF(norm(website)); /* eslint-disable-next-line */ }, [website?.id]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const states = website?.states || {};
  const webhookUrl = website?.form_webhook_token ? `${process.env.REACT_APP_BACKEND_URL}/api/public/forms/${website.form_webhook_token}` : "";

  // --- WordPress-Connector ---
  const [conn, setConn] = useState({ url: website?.connector_url || "", token: "" });
  const [connBusy, setConnBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [checking2, setChecking2] = useState(false);
  useEffect(() => { setConn({ url: website?.connector_url || "", token: "" }); }, [website?.connector_url]);
  const connConfigured = !!website?.connector_configured;
  const connState = states.connector || { color: "gray", label: "Nicht verbunden" };

  const runCheck = async () => {
    setChecking2(true);
    try {
      const { data } = await api.post(`/websites/${clientId}/check`);
      setReport(data?.report || null);
      toast.success("Live-Prüfung abgeschlossen");
      onChange();
    } catch { toast.error("Prüfung fehlgeschlagen"); } finally { setChecking2(false); }
  };
  const saveConnector = async () => {
    if (!conn.url.trim() || !conn.token.trim()) { toast.error("Connector-URL und Token erforderlich"); return; }
    setConnBusy(true);
    try {
      const { data } = await api.put(`/websites/${clientId}/connector`, { connector_url: conn.url.trim(), connector_token: conn.token.trim() });
      if (data?.ok) toast.success("Connector verbunden & live geprüft");
      else toast.error("Connector-Fehler: " + (data?.error || "unbekannt"));
      setConn((c) => ({ ...c, token: "" }));
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Connector konnte nicht gespeichert werden"); }
    finally { setConnBusy(false); }
  };
  const testConnector = async () => {
    setConnBusy(true);
    try {
      const { data } = await api.post(`/websites/${clientId}/connector/test`);
      data?.ok ? toast.success("Connector erreichbar & gültig") : toast.error("Connector-Fehler: " + (data?.error || "unbekannt"));
    } catch (e) { toast.error(e.response?.data?.detail || "Test fehlgeschlagen"); } finally { setConnBusy(false); }
  };
  const revokeConnector = async () => {
    setConnBusy(true);
    try { await api.delete(`/websites/${clientId}/connector`); toast.success("Connector-Verbindung widerrufen"); onChange(); }
    catch { toast.error("Widerruf fehlgeschlagen"); } finally { setConnBusy(false); }
  };
  const downloadPlugin = async () => {
    try {
      const res = await api.get(`/connector/plugin`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "kewagpage-connector.zip"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download fehlgeschlagen"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/websites/${clientId}`, {
        public_url: f.public_url, wp_version: f.wp_version, wp_admin_url: f.wp_admin_url, wp_manual: !!f.wp_manual,
        theme: f.theme, plugins: f.plugins, plugins_visible: !!f.plugins_visible,
        form_source: f.form_source, form_webhook_active: !!f.form_webhook_active,
        analytics_status: f.analytics_status, backup_status: f.backup_status, backup_mode: f.backup_mode,
        last_backup: f.last_backup, last_content_update: f.last_content_update, last_plugin_check: f.last_plugin_check,
        open_tech_points: f.open_tech_points,
      });
      toast.success("Website-Daten gespeichert");
      onChange();
    } catch { toast.error("Speichern fehlgeschlagen"); } finally { setSaving(false); }
  };

  const rotateWebhook = async () => {
    setRotating(true);
    try { await api.post(`/websites/${clientId}/form-webhook?rotate=true`); toast.success("Webhook erneuert"); onChange(); }
    catch { toast.error("Webhook konnte nicht erneuert werden"); } finally { setRotating(false); }
  };
  const copyWebhook = () => { navigator.clipboard?.writeText(webhookUrl); toast.success("Webhook-URL kopiert"); };

  // --- GA4 (Google Analytics 4) ---
  const [ga4Busy, setGa4Busy] = useState(false);
  const [ga4Saving, setGa4Saving] = useState(false);
  const [ga4Res, setGa4Res] = useState(null);
  const [ga4Status, setGa4Status] = useState(null);
  useEffect(() => { api.get("/admin/ga4-status").then((r) => setGa4Status(r.data)).catch(() => setGa4Status(null)); }, []);
  const ga4Connected = website?.analytics_source === "ga4" && website?.analytics_status === "connected";
  const ga4Err = website?.ga4_last_error;
  const saveGa4Id = async () => {
    setGa4Saving(true);
    try {
      await api.put(`/websites/${clientId}`, { ga4_property_id: (f.ga4_property_id || "").trim() });
      toast.success("GA4-Property-ID gespeichert");
      onChange();
    } catch { toast.error("Speichern fehlgeschlagen"); } finally { setGa4Saving(false); }
  };
  const syncGa4 = async () => {
    setGa4Busy(true); setGa4Res(null);
    try {
      const { data } = await api.post(`/websites/${clientId}/ga4-sync`);
      if (data?.ok) { setGa4Res(data.metrics); toast.success("GA4-Daten erfolgreich abgerufen"); }
      else { toast.error("GA4-Fehler: " + (data?.error || "unbekannt")); }
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "GA4-Synchronisierung fehlgeschlagen"); }
    finally { setGa4Busy(false); }
  };

  const [formTesting, setFormTesting] = useState(false);
  const sendFormTest = async () => {
    setFormTesting(true);
    try { await api.post(`/websites/${clientId}/form-test`); toast.success("Test-Anfrage gesendet — erscheint unter Anfragen"); onChange(); }
    catch { toast.error("Test-Anfrage fehlgeschlagen"); } finally { setFormTesting(false); }
  };
  const an = website?.connector_analytics || null;
  const anState = states.analytics || { color: "gray", label: "—" };
  const sk = website?.site_kit || {};
  const _de_dt = (v) => v ? new Date(v).toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" }) : "—";

  const CHIP = { green: "text-[#A3E635] border-[#A3E635]/40 bg-[#A3E635]/10", amber: "text-amber-300 border-amber-400/40 bg-amber-400/10", red: "text-red-300 border-red-400/40 bg-red-400/10", gray: "text-gray-400 border-white/10 bg-white/5" };
  const StateChip = ({ label, st }) => {
    const s = st || { color: "gray", label: "—" };
    return (
      <div className={`px-3 py-2 rounded-lg border ${CHIP[s.color] || CHIP.gray}`} data-testid={`ws-state-${label}`}>
        <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
        <div className="text-sm font-bold mt-0.5">{s.label}{s.value ? ` · ${s.value}` : ""}</div>
      </div>
    );
  };

  const JaNein = ({ label, ok, value, testid }) => (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/40 px-3 py-2" data-testid={testid}>
      <span className="text-xs text-gray-300">{label}{value ? <span className="block text-[10px] text-gray-500 font-mono mt-0.5 truncate">{value}</span> : null}</span>
      <span className={`inline-flex items-center gap-1 text-xs font-bold shrink-0 ${ok ? "text-[#A3E635]" : "text-gray-500"}`}>
        {ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}{ok ? "Ja" : "Nein"}
      </span>
    </div>
  );

  const Txt = ({ k, label, type = "text", ph = "" }) => (    <label className="block">
      <span className="text-gray-500 text-xs uppercase tracking-wider">{label}</span>
      <input type={type} value={f[k] || ""} placeholder={ph} onChange={(e) => set(k, e.target.value)} data-testid={`ws-edit-${k}`}
        className="mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A3E635]" />
    </label>
  );
  const Sel = ({ k, label, options }) => (
    <label className="block">
      <span className="text-gray-500 text-xs uppercase tracking-wider">{label}</span>
      <select value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`ws-edit-${k}`}
        className="mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A3E635]">
        {options.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
      </select>
    </label>
  );
  const Tgl = ({ k, label, on = "Aktiv", off = "Inaktiv" }) => (
    <button type="button" onClick={() => set(k, !f[k])} data-testid={`ws-edit-${k}`}
      className={`flex items-center justify-between gap-3 w-full px-3 py-2.5 rounded-lg border text-sm ${f[k] ? "border-[#A3E635]/50 bg-[#A3E635]/10 text-white" : "border-white/10 bg-black text-gray-400"}`}>
      <span>{label}</span>
      <span className={`text-xs font-bold ${f[k] ? "text-[#A3E635]" : "text-gray-500"}`}>{f[k] ? on : off}</span>
    </button>
  );

  return (
    <div className="space-y-6" data-testid="ws-editor-root">
      {/* === Vollbreiter Kopfbereich: Live-Status (echte Prüfung) === */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div><h3 className="font-bold">Live-Status</h3><p className="text-xs text-gray-500 mt-0.5">Echte Prüfung der Website. Grün = live geprüft · Gelb = manuell/Demo/Prüfung · Rot = Fehler · Grau = nicht verbunden.</p></div>
          {!readOnly && <button onClick={runCheck} disabled={checking2} data-testid="detail-check-site"
            className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
            <RefreshCw className={`w-4 h-4 ${checking2 ? "animate-spin" : ""}`} /> Live prüfen
          </button>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2.5">
          <StateChip label="Erreichbarkeit" st={states.reachability} />
          <StateChip label="SSL / HTTPS" st={states.ssl} />
          <StateChip label="WordPress" st={states.wordpress} />
          <StateChip label="Connector" st={states.connector} />
          <StateChip label="Theme & Builder" st={states.theme} />
          <StateChip label="Plugins" st={states.plugins} />
          <StateChip label="Formulare" st={states.forms} />
          <StateChip label="Analytics / GA4" st={states.analytics} />
          <StateChip label="Site Kit / Google" st={states.site_kit} />
          <StateChip label="Backup" st={states.backup} />
          <StateChip label="Inhalt" st={states.content} />
        </div>
        {report && (
          <div className="mt-4 rounded-xl border border-white/10 bg-black/40 p-4" data-testid="ws-check-report">
            <div className="flex items-center gap-2 mb-2">
              {report.errors > 0 ? <AlertTriangle className="w-4 h-4 text-red-400" /> : report.warnings > 0 ? <AlertTriangle className="w-4 h-4 text-amber-300" /> : <ShieldCheck className="w-4 h-4 text-[#A3E635]" />}
              <span className="font-bold text-sm">Prüfbericht: {report.summary}</span>
              <span className="text-xs text-gray-500 ml-auto">{report.ok} ok · {report.warnings} Warnung · {report.errors} Fehler · {report.not_connected} offen</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
              {report.items.map((it) => (
                <div key={it.key} className="flex items-center gap-2 text-xs">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${it.color === "green" ? "bg-[#A3E635]" : it.color === "red" ? "bg-red-400" : it.color === "amber" || it.color === "blue" ? "bg-amber-300" : "bg-gray-600"}`}></span>
                  <span className="text-gray-400 w-40 shrink-0">{it.label}</span>
                  <span className="text-gray-200">{it.status}{it.value ? ` · ${it.value}` : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {website?.last_checked && (
          <p className="text-[11px] text-gray-500 mt-3" data-testid="ws-admin-last-checked">
            Zuletzt live geprüft: {website.last_checked ? new Date(website.last_checked).toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" }) : "—"}
            {website.live_http_status ? ` · HTTP ${website.live_http_status}` : ""}{website.live_response_ms != null ? ` · ${website.live_response_ms} ms` : ""}
            {website.live_error ? ` · Hinweis: ${website.live_error}` : ""}
          </p>
        )}
      </Card>

      {/* === 12-Spalten-Workspace === */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">

        {/* ===== LINKS (5): WordPress-Connector + Formular-Anbindung ===== */}
        <div className="xl:col-span-5 space-y-6">

          {/* WordPress-Connector (echte Live-Verbindung) */}
          <Card className="p-6" data-testid="ws-connector-card">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: connState.color === "green" ? "rgba(163,230,53,.12)" : connState.color === "red" ? "rgba(248,113,113,.12)" : "rgba(255,255,255,.05)" }}>
                  <PlugZap className={`w-5 h-5 ${connState.color === "green" ? "text-[#A3E635]" : connState.color === "red" ? "text-red-400" : "text-gray-400"}`} />
                </div>
                <div>
                  <h3 className="font-bold">WordPress-Connector</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Echte Live-Daten (Plugins, Theme, Backup) — token-gesichert, keine Passwörter.</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border ${CHIP[connState.color] || CHIP.gray}`} data-testid="ws-connector-status">{connState.label}</span>
                <button onClick={downloadPlugin} data-testid="ws-connector-download" className="bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold py-1.5 px-3 rounded-lg text-xs flex items-center gap-1.5">
                  <Download className="w-3.5 h-3.5" /> Plugin (.zip)
                </button>
              </div>
            </div>

            {!readOnly && <><div className="grid sm:grid-cols-2 gap-3 mb-3">
              <label className="block">
                <span className="text-gray-500 text-xs uppercase tracking-wider">Connector-URL (aus WP-Admin)</span>
                <input value={conn.url} onChange={(e) => setConn((c) => ({ ...c, url: e.target.value }))} placeholder="https://kunde.ch/wp-json/kewagpage/v1/status" data-testid="ws-connector-url"
                  className="mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#A3E635]" />
              </label>
              <label className="block">
                <span className="text-gray-500 text-xs uppercase tracking-wider">Connector-Token</span>
                <input type="password" value={conn.token} onChange={(e) => setConn((c) => ({ ...c, token: e.target.value }))} placeholder={connConfigured ? "•••••• (gespeichert – nur bei Änderung neu eingeben)" : "Token aus WP-Admin einfügen"} data-testid="ws-connector-token"
                  className="mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#A3E635]" />
              </label>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={saveConnector} disabled={connBusy} data-testid="ws-connector-save"
                className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
                <PlugZap className="w-4 h-4" /> {connConfigured ? "Aktualisieren & testen" : "Verbinden & testen"}
              </button>
              {connConfigured && (
                <>
                  <button onClick={testConnector} disabled={connBusy} data-testid="ws-connector-test" className="bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold py-2 px-4 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
                    <RefreshCw className={`w-4 h-4 ${connBusy ? "animate-spin" : ""}`} /> Erneut testen
                  </button>
                  <button onClick={revokeConnector} disabled={connBusy} data-testid="ws-connector-revoke" className="border border-red-400/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 font-semibold py-2 px-4 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
                    <Trash2 className="w-4 h-4" /> Widerrufen
                  </button>
                </>
              )}
            </div></>}

            {connConfigured && connState.color === "green" && (
              <div className="mt-5 space-y-4" data-testid="ws-connector-live">
                <div className="grid sm:grid-cols-3 gap-2.5 text-sm">
                  <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">WordPress</div><div className="font-bold mt-0.5">{website.connector_wp_version || "—"}{website.connector_core_update ? <span className="text-amber-300 text-xs ml-1">Update!</span> : ""}</div></div>
                  <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">Theme · Builder</div><div className="font-bold mt-0.5">{[website.connector_theme, website.connector_builder].filter(Boolean).join(" · ") || "—"}</div></div>
                  <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">PHP</div><div className="font-bold mt-0.5">{website.connector_php_version || "—"}</div></div>
                </div>

                {/* Backup-Karte */}
                <div className="rounded-lg border border-white/10 bg-black/40 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Backup</div>
                  <div className="text-sm">{website.connector_backup_detected ? <>
                    <span className="font-bold">{website.connector_backup_plugin}</span>
                    {website.connector_last_backup ? <span className="text-gray-400"> · Letztes: {new Date(website.connector_last_backup).toLocaleDateString("de-CH")}</span> : <span className="text-amber-300"> · kein Backup-Zeitpunkt</span>}
                    {website.connector_last_backup_status === "failed" && <span className="text-red-400"> · fehlgeschlagen</span>}
                    {website.connector_next_backup ? <span className="text-gray-500"> · Nächstes: {new Date(website.connector_next_backup).toLocaleDateString("de-CH")}</span> : ""}
                  </> : <span className="text-red-300">Kein Backup-Plugin erkannt</span>}</div>
                </div>

                {/* Plugins-Tabelle */}
                {Array.isArray(website.connector_plugins) && website.connector_plugins.length > 0 && (
                  <div className="rounded-lg border border-white/10 overflow-hidden" data-testid="ws-connector-plugins">
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-white/5 text-[10px] uppercase tracking-wider text-gray-500">
                      <div className="col-span-5">Plugin</div><div className="col-span-2">Version</div><div className="col-span-2">Aktiv</div><div className="col-span-3">Update</div>
                    </div>
                    {website.connector_plugins.map((p) => (
                      <div key={p.slug} className="grid grid-cols-12 gap-2 px-3 py-2 text-xs border-t border-white/5 items-center">
                        <div className="col-span-5 font-medium text-gray-200 truncate">{p.name}{p.critical && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 align-middle">kritisch</span>}</div>
                        <div className="col-span-2 text-gray-400 font-mono">{p.version}</div>
                        <div className="col-span-2">{p.active ? <span className="text-[#A3E635]">Aktiv</span> : <span className="text-gray-500">Inaktiv</span>}</div>
                        <div className="col-span-3">{p.update_available ? <span className={p.critical ? "text-red-300" : "text-amber-300"}>Ja{p.new_version ? ` (${p.new_version})` : ""}</span> : <span className="text-gray-500">Aktuell</span>}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Warnungen */}
                {Array.isArray(website.connector_warnings) && website.connector_warnings.length > 0 && (
                  <div className="rounded-lg border border-amber-400/30 bg-amber-400/10 p-3" data-testid="ws-connector-warnings">
                    <div className="flex items-center gap-1.5 text-amber-300 text-xs font-bold mb-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Offene technische Warnungen</div>
                    <ul className="text-xs text-amber-200/90 space-y-0.5 list-disc list-inside">
                      {website.connector_warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                )}
                {website.connector_last_sync && <p className="text-[11px] text-gray-500">Letzter Connector-Abruf: {new Date(website.connector_last_sync).toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" })}</p>}
              </div>
            )}
            {connConfigured && connState.color === "red" && (
              <p className="mt-4 text-xs text-red-300" data-testid="ws-connector-error">Connector-Fehler: {website.connector_error || "nicht erreichbar"}. Bitte URL/Token prüfen und erneut testen.</p>
            )}
          </Card>

          {/* D) Formular-Anbindung (nur KEWAG) */}
          <Card className="p-6" data-testid="ws-forms-card">
            <h3 className="font-bold mb-1">Formular-Anbindung (nur KEWAG)</h3>
            <p className="text-xs text-gray-500 mb-4">Das Connector-Plugin übergibt WP Forms / Contact Form 7 / Fluent Forms automatisch. Diese URL im Plugin (Einstellungen → KEWAGpage Connector) hinterlegen. Eingehende Formulare erzeugen automatisch eine echte Anfrage beim Kunden.</p>
            <div className="grid sm:grid-cols-3 gap-2.5 mb-4 text-sm">
              <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">Erkanntes Formular-Plugin</div><div className="font-bold mt-0.5">{(states.connector?.color === "green" && website.connector_forms_detected) ? (website.connector_forms_plugin || "erkannt") : <span className="text-gray-500">—</span>}</div></div>
              <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">Letzte Anfrage</div><div className="font-bold mt-0.5">{_de_dt(website.form_last_submission_at)}</div></div>
              <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">Letzte Quelle</div><div className="font-bold mt-0.5 truncate">{website.form_last_source || <span className="text-gray-500">—</span>}</div></div>
            </div>
            {!readOnly && <><div className="flex flex-wrap items-center gap-2 mb-3">
              <input readOnly value={webhookUrl} data-testid="ws-webhook-url"
                className="flex-1 min-w-0 basis-48 bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-gray-300 font-mono" />
              <button onClick={copyWebhook} disabled={!webhookUrl} data-testid="ws-webhook-copy"
                className="bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold py-2 px-3 rounded-lg text-sm disabled:opacity-50">Kopieren</button>
              <button onClick={rotateWebhook} disabled={rotating} data-testid="ws-webhook-rotate"
                className="bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold py-2 px-3 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-60">
                <RefreshCw className={`w-3.5 h-3.5 ${rotating ? "animate-spin" : ""}`} /> Erneuern
              </button>
            </div>
            <div className="flex items-center justify-between gap-3">
              <Tgl k="form_webhook_active" label="Webhook aktiv (im WordPress eingerichtet)" on="Aktiv" off="Aus" />
              <div className="text-sm text-gray-400 shrink-0" data-testid="ws-webhook-count">Eingänge: <span className="font-bold text-white">{website?.form_submissions_count || 0}</span></div>
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button onClick={save} disabled={saving} className="bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold py-1.5 px-4 rounded-lg text-xs">Webhook-Status speichern</button>
              <button onClick={sendFormTest} disabled={formTesting} data-testid="ws-form-test"
                className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-1.5 px-4 rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-60">
                <Send className="w-3.5 h-3.5" /> {formTesting ? "Sende…" : "Test-Anfrage senden"}
              </button>
            </div></>}
            {readOnly && <div className="text-sm text-gray-400" data-testid="ws-webhook-count">Eingänge: <span className="font-bold text-white">{website?.form_submissions_count || 0}</span></div>}
          </Card>
        </div>

        {/* ===== MITTE (4): Analytics Light + GA4 + Site Kit ===== */}
        <div className="xl:col-span-4 space-y-6">

          {/* E) KEWAGPage Analytics Light (echtes Tracking via Connector) */}
          <Card className="p-6" data-testid="ws-analytics-card">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: anState.color === "green" ? "rgba(163,230,53,.12)" : anState.color === "amber" || anState.color === "check" ? "rgba(251,191,36,.12)" : "rgba(255,255,255,.05)" }}>
                  <Activity className={`w-5 h-5 ${anState.color === "green" ? "text-[#A3E635]" : (anState.color === "amber" || anState.color === "check") ? "text-amber-300" : "text-gray-400"}`} />
                </div>
                <div>
                  <h3 className="font-bold">KEWAGPage Analytics Light</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Datenschutzfreundliches Tracking — keine IP, keine Profile, keine Cookies. GA4 bleibt separat.</p>
                </div>
              </div>
              <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border ${CHIP[anState.color] || CHIP.gray}`} data-testid="ws-analytics-status">{anState.label}</span>
            </div>

            {an && an.tracking_active ? (
              <div className="space-y-4" data-testid="ws-analytics-live">
                <div className="grid grid-cols-2 gap-2.5">
                  <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">Besucher heute</div><div className="text-xl font-black mt-0.5">{an.today?.visitors ?? 0}</div></div>
                  <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">Besucher 30 Tage</div><div className="text-xl font-black mt-0.5">{an.last_30d?.visitors ?? 0}<span className={`text-xs ml-1 ${(an.delta_pct||0) >= 0 ? "text-[#A3E635]" : "text-red-400"}`}>{(an.delta_pct||0) >= 0 ? "+" : ""}{an.delta_pct||0}%</span></div></div>
                  <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" /> Telefon-Klicks</div><div className="text-xl font-black mt-0.5">{an.events?.phone_clicks ?? 0}</div></div>
                  <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">Formular-Absendungen</div><div className="text-xl font-black mt-0.5">{an.events?.form_submits ?? 0}</div></div>
                  <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">Seitenaufrufe 30 T.</div><div className="text-lg font-bold mt-0.5">{an.last_30d?.pageviews ?? 0}</div></div>
                  <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">E-Mail-Klicks</div><div className="text-lg font-bold mt-0.5">{an.events?.email_clicks ?? 0}</div></div>
                  <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">Formular-Aufrufe</div><div className="text-lg font-bold mt-0.5">{an.events?.form_views ?? 0}</div></div>
                  <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">PDF-Downloads</div><div className="text-lg font-bold mt-0.5">{an.events?.pdf_downloads ?? 0}</div></div>
                </div>
                {Array.isArray(an.top_pages) && an.top_pages.length > 0 && (
                  <div className="rounded-lg border border-white/10 overflow-hidden" data-testid="ws-analytics-top">
                    <div className="px-3 py-2 bg-white/5 text-[10px] uppercase tracking-wider text-gray-500">Top-Seiten (30 Tage)</div>
                    {an.top_pages.slice(0, 5).map((p, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-xs border-t border-white/5">
                        <span className="text-gray-300 truncate">{p.title || p.path}</span>
                        <span className="font-bold text-gray-200">{p.views}</span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-gray-500" data-testid="ws-analytics-sync">Letzter Datenabruf: {_de_dt(website.analytics_last_sync || website.connector_last_sync)}</p>
              </div>
            ) : an ? (
              <p className="text-xs text-amber-300" data-testid="ws-analytics-pending">Tracking ist installiert, aber es liegen noch keine Besuchsdaten vor. Sobald die ersten Besucher kommen, erscheinen hier echte Zahlen.</p>
            ) : (
              <p className="text-xs text-gray-400" data-testid="ws-analytics-off">Noch nicht aktiv. Connector verbinden und im WordPress-Plugin „KEWAGPage Analytics Light" aktivieren. GA4 ist separat <span className="text-gray-300">nicht verbunden</span>.</p>
            )}
          </Card>

          {/* E2) Google Analytics 4 (GA4) — echte Daten via Data API */}
          <Card className="p-6" data-testid="ws-ga4-card">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: ga4Connected ? "rgba(163,230,53,.12)" : ga4Err ? "rgba(248,113,113,.12)" : "rgba(255,255,255,.05)" }}>
                  <BarChart3 className={`w-5 h-5 ${ga4Connected ? "text-[#A3E635]" : ga4Err ? "text-red-300" : "text-gray-400"}`} />
                </div>
                <div>
                  <h3 className="font-bold">Google Analytics 4 (GA4)</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Echte Besucher- &amp; Conversion-Daten der letzten 30 Tage direkt aus GA4.</p>
                </div>
              </div>
              <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border ${ga4Connected ? CHIP.green : ga4Err ? CHIP.red : CHIP.gray}`} data-testid="ws-ga4-status">
                {ga4Connected ? "Live verbunden" : ga4Err ? "Fehler" : "Nicht verbunden"}
              </span>
            </div>

            {/* Konfigurations-Hinweis, wenn Service-Account fehlt */}
            {ga4Status && !ga4Status.enabled && (
              <div className="rounded-lg border border-amber-400/30 bg-amber-400/[0.06] p-3 mb-4 text-xs text-amber-200" data-testid="ws-ga4-notconfigured">
                GA4 ist systemweit noch nicht konfiguriert. Bitte hinterlegen Sie den Service-Account-JSON (Umgebungsvariable GA4_SA_JSON).
                {ga4Status.config_error ? <span className="block mt-1 text-amber-300/80">Hinweis: {ga4Status.config_error}</span> : null}
              </div>
            )}
            {ga4Status && ga4Status.enabled && ga4Status.service_account_email && (
              <div className="rounded-lg border border-white/10 bg-black/40 p-3 mb-4 text-[11px] text-gray-400" data-testid="ws-ga4-sa">
                <span className="flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Service-Account (in GA4 als „Betrachter" hinzufügen):</span>
                <span className="block mt-1 font-mono text-gray-200 break-all">{ga4Status.service_account_email}</span>
              </div>
            )}

            {!readOnly && <div className="grid sm:grid-cols-[1fr_auto_auto] gap-2.5 items-end">
              <label className="block">
                <span className="text-gray-500 text-xs uppercase tracking-wider">GA4-Property-ID (numerisch)</span>
                <input type="text" value={f.ga4_property_id || ""} placeholder="z.B. 123456789" onChange={(e) => set("ga4_property_id", e.target.value)} data-testid="ws-ga4-property-id"
                  className="mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A3E635]" />
              </label>
              <button onClick={saveGa4Id} disabled={ga4Saving} data-testid="ws-ga4-save"
                className="bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold py-2 px-4 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-60">
                <Save className="w-4 h-4" /> Speichern
              </button>
              <button onClick={syncGa4} disabled={ga4Busy || !f.ga4_property_id || (ga4Status && !ga4Status.enabled)} data-testid="ws-ga4-sync"
                className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-4 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-50">
                <RefreshCw className={`w-4 h-4 ${ga4Busy ? "animate-spin" : ""}`} /> {ga4Busy ? "Lädt …" : "Jetzt synchronisieren"}
              </button>
            </div>}

            <p className="text-[11px] text-gray-500 mt-2">
              Property-ID finden: GA4 → Verwaltung → Property-Einstellungen (numerisch, nicht „G-XXXX"). Der Service-Account braucht die Rolle „Betrachter" in dieser Property.
            </p>

            {ga4Err && !ga4Connected && (
              <p className="text-xs text-red-300 mt-3" data-testid="ws-ga4-error">Letzter Fehler: {ga4Err}</p>
            )}

            {(ga4Res || ga4Connected) && (
              <div className="grid grid-cols-2 gap-2.5 mt-4" data-testid="ws-ga4-metrics">
                <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">Besucher 30 Tage</div><div className="text-xl font-black mt-0.5">{ga4Res?.total_visits ?? "—"}{ga4Res && typeof ga4Res.delta_pct === "number" ? <span className={`text-xs ml-1 ${ga4Res.delta_pct >= 0 ? "text-[#A3E635]" : "text-red-400"}`}>{ga4Res.delta_pct >= 0 ? "+" : ""}{ga4Res.delta_pct}%</span> : null}</div></div>
                <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">Seitenaufrufe 30 T.</div><div className="text-xl font-black mt-0.5">{ga4Res?.total_pageviews ?? "—"}</div></div>
                <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" /> Telefon-Klicks</div><div className="text-xl font-black mt-0.5">{ga4Res?.phone_clicks ?? "—"}</div></div>
                <div className="rounded-lg border border-white/10 bg-black/40 p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">Formular-Absendungen</div><div className="text-xl font-black mt-0.5">{ga4Res?.form_submits ?? "—"}</div></div>
              </div>
            )}
            <p className="text-[11px] text-gray-500 mt-3" data-testid="ws-ga4-sync-info">Letzter GA4-Abruf: {_de_dt(ga4Connected ? website.analytics_last_sync : website?.ga4_last_sync)}</p>
          </Card>

          {/* E3) Site Kit by Google — ehrliche Erkennung (Phase 1, keine echten Zahlen) */}
          <Card className="p-6" data-testid="ws-sitekit-card">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: sk.color === "green" ? "rgba(163,230,53,.12)" : sk.color === "amber" ? "rgba(251,191,36,.12)" : "rgba(255,255,255,.05)" }}>
                  <Chrome className={`w-5 h-5 ${sk.color === "green" ? "text-[#A3E635]" : sk.color === "amber" ? "text-amber-300" : "text-gray-400"}`} />
                </div>
                <div>
                  <h3 className="font-bold">Site Kit by Google</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Ehrliche Erkennung über den Connector. Grün nur bei aktivem Site Kit mit verbundenem GA4. Keine Fantasiewerte.</p>
                </div>
              </div>
              <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border ${CHIP[sk.color] || CHIP.gray}`} data-testid="ws-sitekit-status">{sk.label || "Nicht verbunden"}</span>
            </div>

            {connState.color !== "green" ? (
              <p className="text-xs text-gray-400" data-testid="ws-sitekit-noconn">Connector ist nicht live verbunden. Sobald der WordPress-Connector (Plugin ab v1.2.0) verbunden ist, wird der Site-Kit-Status hier ehrlich erkannt.</p>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="ws-sitekit-flags">
                  <JaNein label="Site Kit installiert" ok={sk.installed} testid="sk-installed" />
                  <JaNein label="Site Kit aktiv" ok={sk.active} testid="sk-active" />
                  <JaNein label="GA4 verbunden" ok={sk.ga4_connected} testid="sk-ga4" />
                  <JaNein label="Search Console verbunden" ok={sk.search_console_connected} testid="sk-sc" />
                  <JaNein label="PageSpeed verfügbar" ok={sk.pagespeed_available} testid="sk-psi" />
                  <JaNein label="Property-ID vorhanden" ok={sk.property_id_present} value={sk.property_id || sk.measurement_id} testid="sk-propid" />
                </div>
                <div className="mt-4 flex items-center justify-between gap-3 flex-wrap rounded-lg border border-white/10 bg-black/40 p-3" data-testid="ws-sitekit-source">
                  <div className="text-xs"><span className="text-gray-500 uppercase tracking-wider text-[10px]">Datenquelle (aktuell angezeigt)</span><div className="font-bold mt-0.5">{sk.data_source || "Nicht verbunden"}</div></div>
                  <span className="text-[11px] text-gray-500">Phase 1: nur Erkennung — echte Google-Zahlen folgen in Phase 2.</span>
                </div>
              </>
            )}
          </Card>
        </div>

        {/* ===== RECHTS (3, sticky auf Desktop): Interne Notizen & Sofort-Aktionen ===== */}
        <div className="xl:col-span-3">
          <div className="xl:sticky xl:top-6 space-y-6">
            <Card className="p-6">
              <fieldset disabled={readOnly} className="min-w-0">
              <h3 className="font-bold mb-1">Interne Notizen — kein Live-Status</h3>
              <p className="text-xs text-gray-500 mb-4">Nur interne Notfall-Notizen. Diese Felder erscheinen NIE als „verbunden"/grün im Kunden-Cockpit. Der echte Status oben kommt ausschliesslich vom Connector (live).</p>
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                {!readOnly && <button onClick={save} disabled={saving} data-testid="ws-edit-save"
                  className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-5 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
                  <Save className="w-4 h-4" /> {saving ? "Speichern…" : "Speichern"}
                </button>}
                {f.public_url && (
                  <a href={f.public_url} target="_blank" rel="noreferrer" data-testid="ws-edit-visit"
                    className="bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold py-2 px-4 rounded-lg text-sm flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" /> Website öffnen
                  </a>
                )}
              </div>
              <label className="block mb-4">
                <span className="text-gray-500 text-xs uppercase tracking-wider">Offene technische Punkte (erscheint als Hinweis im Cockpit)</span>
                <textarea value={f.open_tech_points || ""} onChange={(e) => set("open_tech_points", e.target.value)} data-testid="ws-edit-open_tech_points" rows={2}
                  className="mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A3E635]" />
              </label>
              <div className="grid grid-cols-1 gap-3">
                <Txt k="public_url" label="Öffentliche Website (URL)" ph="https://kunde.ch" />
                <Txt k="wp_version" label="WordPress-Version (manuell)" ph="z. B. 6.5" />
                <Tgl k="wp_manual" label="WordPress manuell als gepflegt markieren" on="Manuell" off="Aus" />
                <Sel k="analytics_status" label="Analytics / GA4 (intern)" options={[["not_connected", "Nicht verbunden"], ["demo", "Demo-Daten (intern)"]]} />
                <Sel k="backup_mode" label="Backup-Modus" options={[["not_connected", "Nicht verbunden"], ["manual", "Manuell gepflegt"], ["error", "Prüfung nötig"]]} />
                <Txt k="backup_status" label="Backup-Status (Text)" ph="z. B. Täglich (UpdraftPlus)" />
                <Txt k="wp_admin_url" label="WordPress-Admin-URL (nur intern)" ph="https://kunde.ch/wp-admin" />
                <Txt k="theme" label="Theme / Builder" ph="Astra + Elementor" />
                <Txt k="plugins" label="Wichtige Plugins" ph="Yoast SEO, WP Forms …" />
                <Tgl k="plugins_visible" label="Plugin-Liste für Kunde freigeben" on="Sichtbar" off="Verborgen" />
                <Txt k="form_source" label="Formularquelle (manuell)" ph="WP Forms (Kontaktformular)" />
                <Txt k="last_plugin_check" label="Letzter Plugin-/Theme-Check" type="date" />
                <Txt k="last_backup" label="Letztes Backup" type="date" />
                <Txt k="last_content_update" label="Letztes Inhalts-Update" type="date" />
              </div>
              </fieldset>
            </Card>
          </div>
        </div>

      </div>
    </div>
  );
}
